import { Head, Link, router } from "@inertiajs/react"
import { Trash2 } from "lucide-react"

import { FileIcon, HpiDataTable } from "@/components/hpi"
import type { HpiColumn } from "@/components/hpi"
import AppLayout from "@/layouts/app-layout"
import { formatDate } from "@/lib/date-utils"
import {
  companyDocumentPath,
  companyDocumentsPath,
  companyResourcesPath,
  editCompanyDocumentPath,
  newCompanyDocumentPath,
} from "@/routes"
import type { BreadcrumbItem } from "@/types"

/* ── Types ──────────────────────────────────────────────── */

interface Document {
  id: number
  name: string
  company_name: string | null
  document_date: string | null
  notes: string | null
  filename: string | null
  file_type: string | null
  file_url: string | null
  last_changed: string | null
  last_changed_by: string | null
  status: number
}

interface Props {
  documents: Document[]
  can_create: boolean
  can_destroy: boolean
}

/* ── Helpers ────────────────────────────────────────────── */

/* ── Columns ───────────────────────────────────────────── */

const baseColumns: HpiColumn<Document>[] = [
  {
    header: "Date",
    width: "max-w-[120px]",
    render: (row) => (
      <div className="text-sm">{formatDate(row.document_date)}</div>
    ),
  },
  {
    header: "Description",
    render: (row) => (
      <div className="text-hpi-dark-gray text-sm font-medium">{row.name}</div>
    ),
  },
  {
    header: "Notes",
    render: (row) => (
      <div className="text-hpi-dark-gray text-sm">{row.notes ?? ""}</div>
    ),
  },
  {
    header: "File",
    width: "max-w-[120px]",
    render: (row) =>
      row.filename ? (
        row.file_url ? (
          <a
            href={row.file_url}
            target="_blank"
            rel="noopener noreferrer"
            className="text-hpi-active-blue flex items-center gap-1 text-sm hover:underline"
            onClick={(e) => e.stopPropagation()}
          >
            <FileIcon fileType={row.file_type} className="h-4 w-4" />
            <span className="truncate">{row.filename}</span>
          </a>
        ) : (
          <div className="flex items-center gap-1 text-sm">
            <FileIcon fileType={row.file_type} className="h-4 w-4" />
            <span className="truncate">{row.filename}</span>
          </div>
        )
      ) : (
        <span className="text-hpi-med-gray text-sm">-</span>
      ),
  },
  {
    header: "Last Changed",
    width: "max-w-[150px]",
    render: (row) => (
      <div className="text-hpi-med-blue text-sm">
        {row.last_changed
          ? `${formatDate(row.last_changed)}${row.last_changed_by ? ` by ${row.last_changed_by}` : ""}`
          : ""}
      </div>
    ),
  },
]

const editColumn: HpiColumn<Document> = {
  header: "",
  width: "max-w-[60px]",
  render: (row) => (
    <Link
      href={editCompanyDocumentPath(row.id)}
      className="text-hpi-active-blue text-sm hover:underline"
    >
      Edit
    </Link>
  ),
}

const deleteColumn: HpiColumn<Document> = {
  header: "",
  width: "max-w-[40px]",
  render: (row) => (
    <button
      onClick={(e) => {
        e.stopPropagation()
        if (confirm("Are you sure you want to delete this document?")) {
          router.delete(companyDocumentPath(row.id))
        }
      }}
      className="text-red-400 hover:text-red-600"
    >
      <Trash2 className="h-4 w-4" />
    </button>
  ),
}

/* ── Main Component ────────────────────────────────────── */

const breadcrumbs: BreadcrumbItem[] = [
  { title: "Company", href: companyResourcesPath() },
  { title: "Documents", href: companyDocumentsPath() },
]

export default function DocumentsIndex({
  documents,
  can_create,
  can_destroy,
}: Props) {
  const columns = [
    ...baseColumns,
    ...(can_create ? [editColumn] : []),
    ...(can_destroy ? [deleteColumn] : []),
  ]

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title="Company Documents" />

      <div className="p-6">
        {can_create && (
          <div className="mb-4 flex justify-end">
            <Link
              href={newCompanyDocumentPath()}
              className="bg-hpi-dark-blue hover:bg-hpi-blue rounded px-4 py-2 text-white"
            >
              Add Document
            </Link>
          </div>
        )}

        <HpiDataTable<Document>
          title="Company Documents"
          columns={columns}
          data={documents}
          keyFn={(row) => row.id}
          rowHref={(row) => companyDocumentPath(row.id)}
        />
      </div>
    </AppLayout>
  )
}
